import {FC, useMemo} from 'react';

import {Form, Formik} from 'formik';
import * as Yup from 'yup';

import {Box, Stack} from '@mui/material';

import {EmpionButton} from '../EmpionButton/EmpionButton';

import FormItem from './Item';
import {FormSection} from './types';
import {empionOrange} from '../../layouts/theme/EmpionColors';

const FormMaker: FC<{
  sections: FormSection[];
  onSubmit: (...args: any[]) => any;
  submitLabel?: string;
  secondaryButton?: {
    label: string;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  };
}> = ({sections, onSubmit, submitLabel, secondaryButton}) => {
  const initialValues = useMemo(() => {
    const values: any = {};
    function updateValue(section: FormSection) {
      switch (section.type) {
        case 'title':
        case 'subtitle':
        case 'label':
        case 'description':
          break;
        case 'stacked': {
          section.data.sections.forEach((s) => {
            updateValue(s);
          });
          break;
        }
        default: {
          const name = section.data.name;
          values[name] =
            section.data.initialValue && typeof section.data.initialValue === 'string'
              ? section.data.initialValue.replace(/<[^>]*>/g, '') // data now comes as rich text from BE.
              : section.data.initialValue;
        }
      }
    }
    sections.forEach((section) => {
      updateValue(section);
    });
    return values;
  }, [sections]);
  const schema = useMemo(() => {
    const schemaObj: any = {};
    const updateSchema = (section: FormSection) => {
      switch (section.type) {
        case 'text':
        case 'select':
        case 'textarea':
        case 'checkbox':
        case 'file':
        case 'radio': {
          const name = section.data.name;
          schemaObj[name] = section.data.schema;
          break;
        }
        case 'stacked': {
          section.data.sections.forEach((s) => {
            updateSchema(s);
          });
          break;
        }
      }
    };
    sections.forEach((section) => {
      updateSchema(section);
    });
    return Yup.object().shape(schemaObj);
  }, [sections]);
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={(values) => {
        onSubmit(values);
      }}
    >
      {({errors, touched}) => {
        const titleSection = sections.find((section) => section.type === 'title');
        const bodySections = sections.filter((section) => section.type !== 'title');
        return (
          <Form>
            {titleSection && <FormItem opts={titleSection} errors={errors} touched={touched} />}
            <Box
              sx={{
                mt: titleSection ? 2 : 0,
                px: {xs: 2, sm: 3},
                py: {xs: 2, sm: 2.5},
                border: `1px solid ${empionOrange[3]}`,
                borderRadius: '12px',
                backgroundColor: empionOrange[7],
                // Fields default to the same light grey as this card; force them
                // white so they stand out against the card background.
                '& .MuiOutlinedInput-root.MuiOutlinedInput-root': {backgroundColor: '#fff'},
                '& .MuiOutlinedInput-root.MuiOutlinedInput-root:hover': {backgroundColor: '#fff'},
                '& .MuiOutlinedInput-root.MuiOutlinedInput-root.Mui-focused': {backgroundColor: '#fff'},
              }}
            >
              {bodySections.map((section, idx) => {
                return <FormItem key={idx} opts={section} errors={errors} touched={touched} />;
              })}
            </Box>
            <Stack direction={'row'} spacing={2} sx={{mt: 4}}>
              {secondaryButton && (
                <EmpionButton fullWidth onClick={secondaryButton.onClick} type='button' variant='secondary'>
                  {secondaryButton.label}
                </EmpionButton>
              )}
              <EmpionButton fullWidth type='submit' role='submit'>
                {submitLabel || 'Submit'}
              </EmpionButton>
            </Stack>
          </Form>
        );
      }}
    </Formik>
  );
};

export default FormMaker;
